import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';

const timeline = [
  { year: '2024.01', title: '一个念头', desc: '创始人阿干在与拖延症斗争数年后，突然产生一个念头：「如果让更多人掌握行动的方法会怎样？」当天晚上，他注册了"就去干"域名。' },
  { year: '2024.03', title: '第一篇内容', desc: '「就去干」的第一篇文章《5秒法则》发布在社交媒体，三天内获得 10 万+ 阅读，数百条留言讲述自己的拖延故事。' },
  { year: '2024.06', title: '社群诞生', desc: '第一批 500 位「行动者」聚集在一起，每天打卡、分享、互相鼓励。行动力社群正式起航。' },
  { year: '2024.10', title: '方法论体系化', desc: '经过半年积累，将零散的行动方法整理成系统化的「四大法则」体系，并推出了第一版行动力训练计划。' },
  { year: '2025.03', title: '10 万行动者', desc: '平台累计影响了超过 10 万人开始行动。收到上千封感谢信，有人减掉了 20 公斤，有人换了理想的工作，有人完成了人生的第一个马拉松。' },
  { year: '2025.08', title: '内容升级', desc: '引入神经科学和心理学专家团队，将所有内容进行了科学化升级，确保每一篇文章都有研究支撑。' },
  { year: '2026.01', title: '全新出发', desc: '「就去干」平台全面升级，推出全新的网站和内容体系。我们的使命不变：帮每一个人找回行动的力量。' },
];

const faqData = [
  { q: '「就去干」到底是什么？', a: '「就去干」是一个行动力觉醒平台，也是一套完整的人生哲学。我们通过科学的方法论、真实的案例和持续的社群支持，帮助普通人打破拖延的恶性循环，找回立即行动的能力。我们不卖鸡汤，只提供可操作的方法。' },
  { q: '怎么才能克服拖延症？', a: '克服拖延症的核心不是意志力，而是方法。我们的建议是：从最小的行动开始（比如每天1个俯卧撑），使用5秒法则打断犹豫回路，并建立不可协商的每日习惯。你可以从阅读我们的「行动方法论」分类开始。' },
  { q: '「就去干」的方法有科学依据吗？', a: '我们的所有方法论都基于神经科学、行为心理学和认知科学的研究成果。包括前额叶皮层决策机制、习惯形成的神经可塑性原理、以及微习惯的行为激活效应等。我们也有专业的心理学顾问团队把关内容质量。' },
  { q: '我能为这个平台做什么？', a: '最好的支持方式就是——就去干！把你在平台上学到的方法用起来，用行动改变自己的生活。然后，把你的改变故事分享给更多人。改变自己，就是改变世界。' },
];

export default function AboutPage() {
  const canonicalUrl = 'https://jiuqugan.com/about';
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <>
      <SEOHead
        title="关于我们 - 就去干行动力觉醒"
        description="就去干是一个致力于帮助人们打破拖延、立即行动的行动力觉醒平台。了解我们的品牌故事、核心理念和联系方式，加入行动者的行列。"
        keywords="就去干,关于我们,行动力平台,品牌故事,拖延症,执行力"
        canonicalUrl={canonicalUrl}
        ogType="website"
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@graph': [
              {
                '@type': 'Organization',
                name: '就去干',
                url: canonicalUrl,
                description: '行动力觉醒平台，帮助人们打破拖延、立即行动',
                foundingDate: '2024',
                contactPoint: {
                  '@type': 'ContactPoint',
                  contactType: 'customer service',
                  email: 'hello@jiuqugan.com',
                  availableLanguage: ['Chinese'],
                },
              },
              {
                '@type': 'FAQPage',
                mainEntity: faqData.map((item) => ({
                  '@type': 'Question',
                  name: item.q,
                  acceptedAnswer: { '@type': 'Answer', text: item.a },
                })),
              },
              {
                '@type': 'WebPage',
                name: '关于我们 - 就去干',
                url: canonicalUrl,
                isPartOf: { '@type': 'WebSite', name: '就去干' },
              },
            ],
          }),
        }}
      />

      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 md:pt-24">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />

            <section id="brand" className="py-8 md:py-12">
              <h1 className="text-3xl md:text-5xl font-heading font-bold text-foreground-950 mb-6">
                关于「就去干」
              </h1>
              <div className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed">
                <p className="text-base md:text-lg leading-relaxed">
                  「就去干」不是一句空洞的鸡汤，而是一种<strong>彻底打破拖延、立即付诸行动的人生哲学</strong>。
                </p>
                <p className="mt-4">
                  我们相信，每个人的内心都有一个渴望行动的自己。那个自己想减肥、想换工作、想创业、想学新技能——但总有一个声音说「明天再说」「还没准备好」「再想想」。
                </p>
                <p className="mt-4">
                  那个声音不是懒惰，是恐惧。是大脑在试图保护你免受失败和不确定性。
                </p>
                <p className="mt-4">
                  而我们的使命，就是帮你绕过那个声音。用科学的方法、真实的案例、温暖的社群，让你从「想」到「做」的距离变得无限短。
                </p>
              </div>
            </section>

            <section className="py-8 md:py-12" aria-labelledby="mission-title">
              <h2 id="mission-title" className="text-xl md:text-3xl font-heading font-bold text-foreground-950 mb-8">
                我们的使命
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                  { title: '科学', desc: '所有方法论都有研究支撑，不讲玄学，不卖鸡汤', icon: 'ri-flask-line' },
                  { title: '行动', desc: '一切从行动出发，用行动检验理论，在实践中迭代', icon: 'ri-rocket-2-line' },
                  { title: '陪伴', desc: '你不是一个人在战斗，无数行动者和你一起前行', icon: 'ri-group-line' },
                ].map((item) => (
                  <div key={item.title} className="p-6 rounded-lg bg-background-100 border border-background-200/80 text-center">
                    <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                      <i className={`${item.icon} text-lg`}></i>
                    </div>
                    <h3 className="text-base font-heading font-bold text-foreground-950 mb-2">{item.title}</h3>
                    <p className="text-sm text-foreground-500">{item.desc}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="py-8 md:py-12" aria-labelledby="timeline-title">
              <h2 id="timeline-title" className="text-xl md:text-3xl font-heading font-bold text-foreground-950 mb-8">
                品牌历程
              </h2>
              <div className="relative">
                <div className="absolute left-4 md:left-6 top-0 bottom-0 w-px bg-background-300"></div>
                <div className="space-y-8">
                  {timeline.map((item, index) => (
                    <div key={index} className="relative flex items-start gap-4 md:gap-6 pl-12 md:pl-16">
                      <div className="absolute left-2.5 md:left-4 w-3 h-3 md:w-4 md:h-4 rounded-full bg-primary-500 border-2 border-background-50 z-10"></div>
                      <div>
                        <span className="text-xs font-semibold text-primary-500">{item.year}</span>
                        <h3 className="text-base font-heading font-bold text-foreground-950 mt-1 mb-1.5">{item.title}</h3>
                        <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <section id="contact" className="py-8 md:py-12" aria-labelledby="faq-title">
              <h2 id="faq-title" className="text-xl md:text-3xl font-heading font-bold text-foreground-950 mb-8">
                常见问题
              </h2>
              <div className="space-y-3">
                {faqData.map((item, index) => (
                  <div
                    key={index}
                    className="rounded-lg border border-background-200/80 bg-background-100 overflow-hidden"
                  >
                    <button
                      onClick={() => setOpenFaq(openFaq === index ? null : index)}
                      className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer"
                    >
                      <span className="text-sm md:text-base font-medium text-foreground-900 pr-4">
                        {item.q}
                      </span>
                      <i
                        className={`ri-arrow-down-s-line w-4 h-4 flex items-center justify-center text-foreground-400 transition-transform duration-200 flex-shrink-0 ${
                          openFaq === index ? 'rotate-180' : ''
                        }`}
                      ></i>
                    </button>
                    {openFaq === index && (
                      <div className="px-5 pb-4">
                        <p className="text-sm text-foreground-600 leading-relaxed">{item.a}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>

            <section className="py-8 md:py-12 mb-8 md:mb-12" aria-labelledby="contact-title">
              <div className="rounded-lg bg-background-100 border border-background-200/80 p-6 md:p-10 text-center">
                <h2 id="contact-title" className="text-xl md:text-3xl font-heading font-bold text-foreground-950 mb-4">
                  联系我们
                </h2>
                <p className="text-sm md:text-base text-foreground-600 mb-8 max-w-lg mx-auto leading-relaxed">
                  如果你有任何关于行动力的问题、合作意向，或者只是想分享你的「就去干」故事，随时联系我们。
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <a
                    href="mailto:hello@jiuqugan.com"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-medium rounded-full text-sm transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-mail-line w-4 h-4 flex items-center justify-center"></i>
                    hello@jiuqugan.com
                  </a>
                </div>
              </div>
            </section>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}